import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { faltaDeAcceso } from "../lib/acceso";

// AUSENCIAS (3 Nov 2026) -- vacaciones, permisos y descanso de un prestador (`doctor_blocks`).
// Cada acción guarda AL MOMENTO contra el backend y relee la lista: no depende del botón
// Guardar del modal que lo contiene. Quien lo usa lo pinta sólo al EDITAR (el prestador ya
// tiene id); al crear no hay a quién apartarle días.
// 🔴 Se ramifica por `code` top-level (DT.60), nunca por el texto del backend.
// 🔴 Si la lista no se pudo leer no se pinta nada: «Sin ausencias próximas.» sería una
//    afirmación sin dato detrás.

// `sustantivo` es la palabra con la que el modal que lo contiene ya nombra a la persona.
export const TEXTOS_AUSENCIAS = {
  titulo: "Ausencias", // Texto aprobado por Jesús (3 Nov 2026)
  ayuda: (sustantivo) => `En esos días no se ofrecen horarios de este ${sustantivo} para agendar citas.`, // Texto aprobado por Jesús (3 Nov 2026)
  vacia: "Sin ausencias próximas.", // Texto aprobado por Jesús (3 Nov 2026)
  sinMotivo: "Sin motivo", // Texto aprobado por Jesús (3 Nov 2026)
  unDia: (fecha, motivo) => `${fecha} · ${motivo}`, // Texto aprobado por Jesús (3 Nov 2026)
  variosDias: (del, al, motivo) => `${del} al ${al} · ${motivo}`, // Texto aprobado por Jesús (3 Nov 2026)
  del: "Del", // Texto aprobado por Jesús (3 Nov 2026)
  al: "Al", // Texto aprobado por Jesús (3 Nov 2026)
  motivo: "Motivo (opcional)", // Texto aprobado por Jesús (3 Nov 2026)
  agregar: "Agregar ausencia", // Texto aprobado por Jesús (3 Nov 2026)
  guardando: "Guardando…", // Texto aprobado por Jesús (3 Nov 2026)
  quitar: "Quitar", // Texto aprobado por Jesús (3 Nov 2026)
  conCitas: (n, sustantivo) => (n === 1
    ? `Hay 1 cita de este ${sustantivo} en esas fechas. Muévela o cancélala primero.`
    : `Hay ${n} citas de este ${sustantivo} en esas fechas. Muévelas o cancélalas primero.`), // Texto aprobado por Jesús (3 Nov 2026)
  fechasInvalidas: "Revisa las fechas de la ausencia.", // Texto aprobado por Jesús (3 Nov 2026)
  noDisponibles: "Las ausencias todavía no están disponibles.", // Texto aprobado por Jesús (3 Nov 2026)
  error: "No se pudo guardar la ausencia. Intenta de nuevo.", // Texto aprobado por Jesús (3 Nov 2026)
};
const T = TEXTOS_AUSENCIAS;

// Mismo formato que las fechas de la Agenda (`dayLabel` en pages/Agenda.js: es-MX, día de la
// semana corto, día a dos dígitos y mes corto). La fecha llega como «YYYY-MM-DD» y se arma en
// hora LOCAL: `new Date("2026-11-10")` sería medianoche UTC, o sea el día anterior en México.
export function fechaAusencia(iso) {
  const [y, m, d] = String(iso).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("es-MX", { weekday: "short", day: "2-digit", month: "short" });
}

export function textoAusencia(a) {
  const motivo = (a.reason || "").trim() || T.sinMotivo;
  return a.start_date === a.end_date
    ? T.unDia(fechaAusencia(a.start_date), motivo)
    : T.variosDias(fechaAusencia(a.start_date), fechaAusencia(a.end_date), motivo);
}

export function mensajeErrorAusencia(e, sustantivo) {
  const r = e?.response;
  const code = r?.data?.code;
  if (r?.status === 409 && code === "AUSENCIA_CON_CITAS") return T.conCitas(Number(r.data.citas) || 0, sustantivo);
  if (r?.status === 400 && code === "AUSENCIA_FECHAS_INVALIDAS") return T.fechasInvalidas;
  if (r?.status === 503 && code === "AUSENCIAS_NO_DISPONIBLES") return T.noDisponibles;
  // Un 403 por capacidad no se pinta como avería: van las palabras del servidor (COL.12).
  const acceso = faltaDeAcceso(e);
  if (acceso?.detail) return acceso.detail;
  return T.error;
}

const etiqueta = "text-xs font-semibold uppercase tracking-widest text-slate-500";
const campo = "w-full border border-slate-300 bg-white rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20";

export default function AusenciasDoctor({ doctorId, sustantivo }) {
  const [estado, setEstado] = useState("cargando");   // cargando | listo | error
  const [items, setItems] = useState([]);
  const [del, setDel] = useState("");
  const [al, setAl] = useState("");
  const [motivo, setMotivo] = useState("");
  const [ocupado, setOcupado] = useState(null);       // null | "agregar" | id de la que se quita
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    try {
      // Promise.resolve: también un fallo síncrono del cliente cae en el catch.
      const { data } = await Promise.resolve().then(() => api.get(`/doctors/${doctorId}/blocks`));
      setItems(Array.isArray(data?.items) ? data.items : []);
      setEstado("listo");
    } catch {
      setEstado("error");
    }
  }, [doctorId]);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiarDel = (valor) => {
    setDel(valor);
    // Si «Del» queda después de «Al», «Al» toma el valor de «Del» (fechas ISO: se comparan como texto).
    if (valor && al && valor > al) setAl(valor);
  };

  const agregar = async () => {
    if (!del || !al || ocupado) return;
    setOcupado("agregar");
    setError(null);
    try {
      await api.post(`/doctors/${doctorId}/blocks`, { start_date: del, end_date: al, reason: motivo });
      setDel(""); setAl(""); setMotivo("");
      await cargar();
    } catch (e) {
      setError(mensajeErrorAusencia(e, sustantivo));
    } finally {
      setOcupado(null);
    }
  };

  const quitar = async (id) => {
    if (ocupado) return;
    setOcupado(id);
    setError(null);
    try {
      await api.delete(`/doctors/${doctorId}/blocks/${id}`);
      await cargar();
    } catch (e) {
      setError(mensajeErrorAusencia(e, sustantivo));
    } finally {
      setOcupado(null);
    }
  };

  if (estado !== "listo") return null;

  return (
    <div className="mt-4 space-y-2" data-testid="ausencias-doctor">
      <span className={etiqueta}>{T.titulo}</span>
      <p className="text-xs text-slate-500">{T.ayuda(sustantivo)}</p>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="ausencias-vacia">{T.vacia}</p>
      ) : (
        <ul className="space-y-1.5" data-testid="ausencias-lista">
          {items.map((a) => (
            <li key={a.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50" data-testid={`ausencia-${a.id}`}>
              <span className="flex-1 min-w-0 text-sm text-slate-800" data-testid={`ausencia-texto-${a.id}`}>{textoAusencia(a)}</span>
              <button type="button" onClick={() => quitar(a.id)} disabled={!!ocupado}
                className="text-xs text-rose-500 border border-rose-200 rounded-lg px-3 py-1.5 hover:bg-rose-50 disabled:opacity-50"
                data-testid={`ausencia-quitar-${a.id}`}>
                {T.quitar}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="block">
          <span className={etiqueta}>{T.del}</span>
          <input type="date" required value={del} onChange={(e) => cambiarDel(e.target.value)}
            className={`${campo} mt-1`} data-testid="ausencia-del" />
        </label>
        <label className="block">
          <span className={etiqueta}>{T.al}</span>
          <input type="date" required value={al} onChange={(e) => setAl(e.target.value)}
            className={`${campo} mt-1`} data-testid="ausencia-al" />
        </label>
        <label className="block">
          <span className={etiqueta}>{T.motivo}</span>
          <input type="text" maxLength={120} value={motivo} onChange={(e) => setMotivo(e.target.value)}
            className={`${campo} mt-1`} data-testid="ausencia-motivo" />
        </label>
      </div>
      {error && <p role="alert" className="text-sm text-rose-600" data-testid="ausencia-error">{error}</p>}
      <button type="button" onClick={agregar} disabled={!del || !al || !!ocupado}
        className="px-4 py-2 text-sm rounded-lg bg-brand-electric text-white hover:bg-blue-700 disabled:opacity-60"
        data-testid="ausencia-agregar">
        {ocupado === "agregar" ? T.guardando : T.agregar}
      </button>
    </div>
  );
}
